import { useQuery } from "@tanstack/react-query";
import { Brain } from "lucide-react";
import type { FC } from "react";
import React from "react";

import { getUserPresentableMessage } from "@core/foundation/error";
import { bindArgs } from "@core/foundation/function";

import { H, HSection } from "@react/foundation/heading-level";
import { ErrorView, LoadingView } from "@react/foundation/placeholder";

import type { Project } from "@apis/memory/models";

import { PageView } from "../../../shared/components/PageView.tsx";
import { useAppNavigation } from "../../../shared/hooks/useAppNavigation.ts";
import { useQueries } from "../../../shared/hooks/useQueries.ts";

import { ConnectGithubRepoDialog } from "./connect-github-repo-dialog/ConnectGithubRepoDialog.tsx";
import { GithubRepoCard } from "./GithubRepoCard.tsx";
import { MemoryApiKeySection } from "./MemoryApiKeySection.tsx";
import { MemoryList } from "./MemoryList.tsx";
import { RecordLink } from "./RecordLink.tsx";

interface ProjectViewProps {
  readonly projectId: Project["id"];
}

export const ProjectView: FC<ProjectViewProps> = ({ projectId }) => {
  const { goToMemoryProjectComments } = useAppNavigation();

  const { getMemoryProjectQueryOptions } = useQueries();
  const project = useQuery(getMemoryProjectQueryOptions({ id: projectId }));

  if (project.status === "pending") {
    return <LoadingView />;
  }

  if (project.status === "error") {
    return (
      <ErrorView
        description={getUserPresentableMessage(
          project.error,
          "Failed to load the project.",
        )}
      />
    );
  }

  const { githubRepos, project: projectData } = project.data;

  return (
    <PageView icon={<Brain />} subtitle={projectData.name} title="Memory">
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <HSection heading={<H className="text-sm font-semibold">API key</H>}>
            <MemoryApiKeySection project={projectData} />
          </HSection>
        </section>
        <section className="flex flex-col gap-2">
          <HSection heading={<H className="text-sm font-semibold">Records</H>}>
            <div className="flex flex-col gap-2">
              <RecordLink
                description="Review comments pulled from the connected repositories"
                label="Comments"
                onClick={bindArgs(goToMemoryProjectComments, projectData.id)}
              />
            </div>
          </HSection>
        </section>
        <section className="flex flex-col gap-2">
          <HSection
            heading={<H className="text-sm font-semibold">Repositories</H>}
          >
            {githubRepos.map(githubRepo => (
              <GithubRepoCard
                githubRepo={githubRepo}
                key={githubRepo.id}
                projectId={projectData.id}
              />
            ))}
            <ConnectGithubRepoDialog project={projectData} />
          </HSection>
        </section>
        <section className="flex flex-col gap-2">
          <HSection heading={<H className="text-sm font-semibold">Memories</H>}>
            <MemoryList projectId={projectData.id} />
          </HSection>
        </section>
      </div>
    </PageView>
  );
};
